<template>
    <div class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
            <!-- Editing window -->
            <div v-if="!showConfirmationWindow" class="modal-content">
                <div class="modal-body text-center">
                    <button class="modal-close" @click="onClose">
                        <i class="fas fa-lg fa-times"></i>
                    </button>
                    <img src="@/assets/images/rebrandLogo2.png" class="logo" />

                    <!-- Steps progress bar -->
                    <div class="steps-bar-container">
                        <ul class="progressbar">
                            <li :class="{done:isProgressing[key]}" v-for="(step,key) in steps" :key="key">
                                <i class="fas fa-check-circle" :class="{'no-line': key == steps.length - 1}"></i>
                                <span :class="{'active': selected === key}">{{key + 1}}.&nbsp;{{step}}</span>
                            </li>
                        </ul>
                    </div>

                    <refresh-information class="my-3 d-flex justify-content-center"/>

                    <!-- Input area -->
                    <div class="input-area bg-olf-light-greyish-blue mb-3 p-3">
                        <h4>Enter settlement prices</h4>
                        <span v-if="isError" class="error-message d-block my-4">{{propErrorMessage}}</span>
                        
                        <!-- FX Settlement input row -->
                        <div class="row" :class="{'add-padding': !isError}">
                            <h4 
                                class="col-md-12 col-lg-3 title"
                                :class="[$breakpoint.mdAndDown ? 'text-left' : 'mt-4 pt-3']"
                            >
                                FX Settlement price
                            </h4>
                            <div class="col-md-12 col-lg-3 d-flex mb-3 flex-column justify-content-center">
                                <label
                                    :class="{'text-left': $breakpoint.mdAndDown}"
                                >(GBP/USD)</label>
                                <cleave 
                                    :options="numeralFx" 
                                    class="empty-fill white-field text-center" 
                                    :value="propFxSettlement"
                                    @input="$emit('update:propFxSettlement', $event)"
                                    @blur.native="setPriceFormat('FX')"
                                ></cleave>
                            </div>
                        </div>
  
                        <!-- Fuel prices (USD) input row -->
                        <div class="row">
                            <h4 class="col-md-12 col-lg-3 title"
                                :class="[$breakpoint.mdAndDown ? 'text-left' : 'mt-4 pt-3']"
                            >
                                Fuel Settlement Prices ({{ rulesEnum.eUnitType.UsdPerMetricTonne.display }})
                            </h4>
                            <div 
                                class="col-md-4 col-lg-3  d-flex flex-column justify-content-center" 
                                :class="{'mb-2' : $breakpoint.smAndDown}"
                                v-for="product in propBaseProducts" :key="product.fuel_id"
                            >  
                                <label
                                    :class="{'text-left': $breakpoint.mdAndDown}"
                                >{{product.name}}</label>
                                <cleave 
                                    class="empty-fill white-field text-center" 
                                    :options="numeralPositive" 
                                    :value="product.price"
                                    @input="onPriceChange(product.fuel_id, $event)"
                                    @blur.native="setPriceFormat('FUEL', product.fuel_id)"
                                ></cleave>
                            </div>
                        </div>
                    </div>

                    <!-- Fuel prices table -->
                    <div class="row">
                        <div class="col-md-12 table-container">
                            <table class="table responsive-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th v-for="(product, key) in propProductsToShow" :key="key">
                                            {{ product.fuel_id | rawfueltype }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr :class="{'py-2': $breakpoint.smAndDown}">
                                        <th class="text-left">Settle Base Price (ppl)</th>
                                        <td 
                                            v-for="(product, idx) in propProductsToShow" 
                                            :key="`${idx}-${renderKey}`"
                                            :label="product.fuel_id | rawfueltype"
                                        >
                                            <span>{{ product.totalFixed | priceformat }}</span>
                                        </td>
                                    </tr>
                                    <tr :class="{'py-2': $breakpoint.smAndDown}">
                                        <th class="text-left">Market Change (ppl)</th>
                                        <td 
                                            v-for="(product, idx) in propProductsToShow" 
                                            :key="`${idx}-${renderKey}`"
                                            :label="product.fuel_id | rawfueltype"
                                        >
                                            <span>{{ product.marketChange | priceformat }}</span>
                                        </td>
                                    </tr>
                                    <tr :class="{'py-2': $breakpoint.smAndDown}">
                                        <th class="text-left">Live Price (ppl)</th>
                                        <td 
                                            v-for="(product, idx) in propProductsToShow" 
                                            :key="`${idx}-${renderKey}`"
                                            :label="product.fuel_id | rawfueltype"
                                        >
                                            <span>{{ product.totalLive | priceformat }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="modal-footer d-flex justify-content-center">
                    <button class="btn btn-primary" @click="$emit('save-settlements', onSave)" :disabled="isError">Confirm Prices</button>
                    <button v-if="isPricingEngineEnabled" class="btn btn-link text-secondary" @click="openManagePriceRules">Manage Price Rules</button>
                </div>
            </div>

            <!-- Confirmation screen -->
            <div v-else class="modal-content confirmation-window">
                <div class="modal-body text-center mt-4">
                    <img src="/assets/rebrandLogo2.png" class="logo" />
                    <h5 class="my-5">{{confirmationMessage}}</h5>

                    <!-- Display this icon only after seller click 'Confirm' -->
                    <i v-if="!dailyPricesNotSet" class="fas fa-5x" :class="updateSuccessful? 'fa-check-circle text-primary' : ['fa-times-circle', 'text-danger']"></i>
                </div>
                <div class="modal-footer pb-4 d-flex justify-content-center">
                    <!-- Buttons below are for confirming seller closing the popup without daily prices set -->
                    <button class="btn btn-secondary mr-2" @click="setUpDailyPrices">Edit Daily Prices</button>
                    <button data-dismiss="modal" aria-label="Close" class="btn btn-tertiary" @click="closeModal">Close</button>

                </div>
            </div>
        </div>
    </div>
</template>


<script>
import { mapActions, mapGetters } from 'vuex';
import breakpoint from '@/mixins/index';
import { rulesEnum } from "@/util/enums/rules";

import RefreshInformation from '@/components/RefreshInformation.vue';

export default {
    components: {
        RefreshInformation
    },
    mixins: [breakpoint],
    props: {
        propBaseProducts: {
            type: Array,
            required: true,
        },
        propProductsToShow: {
            type: Array,
            required: true,
        },
        propFxSettlement: {
            type: String,
            required: true,
        },
        propErrorMessage: {
            type: String,
            default: "",
        },
        settlementRulesValid: {
            type: Boolean,
            required: true,
        },
        hasExistingSettlementValues: {
            type: Boolean,
            required: true,
        }
    },
    data:function(){
        return{
            showConfirmationWindow: false,
            confirmationMessage: '',
            updateSuccessful: false,
            dailyPricesNotSet: false,
            isProgressing: [],
            steps: ["Settlement Prices" ,"Done!"],
            selected: 0,
            renderKey: 0,
            rulesEnum
        }
    },
    computed:{
        ...mapGetters('inputCheckers', ['numeralPositive', 'numeralFx']),
        ...mapGetters("login", ["isPricingEngineEnabled"]),
        isError: function() {
            return this.propErrorMessage !== "";
        }
    },
    methods:{
        ...mapActions('livePrices',['closeModal']),
        setPriceFormat: function(inputType, fuelId){
            if (inputType == 'FX'){
                const fxSettlement = Number(this.propFxSettlement).toFixed(4)
                this.$emit("update:propFxSettlement", fxSettlement);
            }
            else {
                const fuelMatch = this.propBaseProducts.find(fuel => fuel.fuel_id === fuelId);
                const fuelMatchPrice = Number(fuelMatch.price).toFixed(2);
                this.onPriceChange(fuelId, fuelMatchPrice);
            }
        },
        onPriceChange: function(productId, value) {
            this.$emit('on-price-change', productId, value);
        },
        onSave(isSaved) {
            if (isSaved) {
                this.confirmationMessage = "Settlement prices have been successfully updated";
                this.updateSuccessful = true;
            } else {
                this.confirmationMessage = "Sorry, we are unable to update your settlement prices at the moment";
                this.updateSuccessful = false;
            }
            this.showConfirmationWindow = true;
        },
        onClose(){
            /* Check if this seller has daily prices set, otherwise display a confirmation screen */
            if (!this.settlementRulesValid || !this.hasExistingSettlementValues) {
                this.dailyPricesNotSet = true
                this.confirmationMessage = 'Please be aware that if you do not input settlement prices, your Live Offers will not be displayed'
                this.showConfirmationWindow = true
            }
            else {
                this.hideModal()
                this.closeModal()
            }
        },
        hideModal(){
            let element = this.$root.$refs.dailyPrices.$el
            $(element).modal('hide')
        },
        openManagePriceRules() {
            this.hideModal();
            this.closeModal();
            this.$router.push("/PricingEngine");
        },
        setUpDailyPrices: function(){
            this.dailyPricesNotSet = false
            this.showConfirmationWindow = false
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/_progress.scss";
@import "@/assets/scss/components/_modals.scss";

.mw-225-px {
    max-width: 225px;
}
</style>